import type { GetServerSidePropsContext } from 'next'

import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'

import { getControlToVariantRedirectSlug } from 'experiments/pen-2866_v1_alternative_categories/redirectSlugs'
import {
  getAllCoursesShallow,
  getCoursesWithCategorySlug,
  getDefaultProduct,
  getGuestPassByReferralToken,
  getOutcomesForCourses,
  getV2Categories,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { ALL_CLASSES, JUST_ADDED, TRENDING, redirect } from 'libs/utils'
import { useSprig } from 'libs/sprig'
import { getTrendingInstructors } from 'pages/categories/CategoryPage/dataFetching/getTrendingInstructors'
import { getSubcatsCoursesOutcomes } from 'pages/categories/CategoryPage/dataFetching/getSubcatsCoursesOutcomes'
import { usePersonaInTiersExperimentViewed } from 'experiments/prcng-472_v4_persona_in_tiers'
import { useTrackPen5222Experiment } from 'experiments/pen-5222_v2_money_back_pricing_microcopy'

import { redirectSlugMap } from './redirectSlugs'

import { AllCategoryPage } from './AllCategoryPage'
import { CategoryPage, getSubcategoriesDeprecated } from './CategoryPage'
import { type CategoryPageProps } from './pageProps'

export default function Categories(props: CategoryPageProps) {
  const isAllCatsPage = props.slug === ALL_CLASSES

  useSprig()
  usePersonaInTiersExperimentViewed()
  useTrackPen5222Experiment()

  if (isAllCatsPage) {
    return <AllCategoryPage {...props} />
  }

  return (
    <TrackingParamsProvider
      value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-categories' }}
    >
      <CategoryPage {...props} />
    </TrackingParamsProvider>
  )
}

Categories.pageSettings = {
  analyticsLabel: 'Category',
  showPromo: true,
  hideNavigation: true, // pen-5015_v1 experiment
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug: ctxSlug = [] },
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  // instead of /categories/all-classes being the same as /categories, redirect
  if (ctxSlug[0] === ALL_CLASSES) {
    return redirect(ctx, '/categories')
  }

  const slug = ctxSlug[0] || ALL_CLASSES

  // TODO: remove these redirects
  if ([JUST_ADDED, 'recently-viewed', TRENDING].includes(slug)) {
    return redirect(ctx, '/categories')
  }

  const redirectToVariantSlug = getControlToVariantRedirectSlug(slug)

  if (redirectToVariantSlug) {
    return redirect(ctx, `/categories/${redirectToVariantSlug}`)
  }

  const categories = await getV2Categories(ctx)
  const category = categories.find((c) => c.slug === slug) || null
  if (!category && slug !== ALL_CLASSES) {
    const redirectSlug = redirectSlugMap.get(slug)
    if (redirectSlug) {
      return redirect(ctx, `/categories/${redirectSlug}`)
    }
    return {
      notFound: true,
    }
  }

  const allowAltSet = true

  return getPageData({
    criticalPromises: {
      courses:
        slug === ALL_CLASSES
          ? getAllCoursesShallow(ctx, { allowAltSet })
          : getCoursesWithCategorySlug(ctx, { slug, allowAltSet }),
      subcategories:
        slug !== ALL_CLASSES
          ? getSubcategoriesDeprecated(ctx, slug)
          : Promise.resolve([]),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      guestPass: getGuestPassByReferralToken(ctx, { referralToken }),
      subcatsCoursesOutcome: getSubcatsCoursesOutcomes(),
      instructors: getTrendingInstructors(),
      coursesOutcomes: getOutcomesForCourses(),
    },
    propsMapper: (restProps) => ({
      category,
      categories,
      slug,
      ...restProps,
    }),
  })
}
